import { useEffect, useRef } from 'react';
import { useSelector, useStore } from 'react-redux';
import { useParams } from 'react-router';
import { useAppDispatch } from '../../state/hooks';
import { setLocale } from '../../state/slices/languageSlice';
import { changeLayerStyle, reArrangeSelectedMapLayers, setLegends, removeAllSelectedLayers } from '../../state/slices/rpcSlice';
import { setIsSideMenuOpen, setIsThemeMenuOpen, setSelectedMapLayersMenuTab } from '../../state/slices/uiSlice';
import { Logger } from '../../utils/logger';
import { updateLayers, selectTheme } from '../../utils/rpcUtil';
import { isMobile} from '../../theme/theme';
import { Slide, toast } from "react-toastify";
import strings from '../../translations';

const LOG = new Logger('HandleSharedWebSiteLink');

export const HandleSharedWebSiteLink = () => {
  const { zoom, x, y, maplayers, themeId, lang } = useParams();
  const dispatch = useAppDispatch();
  const store = useStore();           // <- get store object here
  const channel = useSelector(state => state.rpc.channel);
  const allThemesWithLayers = useSelector(state => state.rpc.allThemesWithLayers);
  const handledRef = useRef(false);

  useEffect(() => {
    //if (handledRef.current) return;
    if (!channel) return;
    const z = zoom !== undefined ? Number(zoom) : NaN;
    const xi = x !== undefined ? Number(x) : NaN;
    const yi = y !== undefined ? Number(y) : NaN;
    const hasCoords = Number.isFinite(z) && Number.isFinite(xi) && Number.isFinite(yi);
    const hasZoomOnly = Number.isFinite(z) && (!Number.isFinite(xi) || !Number.isFinite(yi));
    const hasTheme = !!themeId;

    if (!(hasCoords || hasZoomOnly || hasTheme || maplayers)) {
      return;
    }

    if ((!isNaN(zoom) && x && y) || themeId) {
        LOG.log('The page was accessed via a link, initializing the map according to the link.');
    } else {
        return (<></>);
    }

    // if channel ready and zoom given without xx and y, zoom map to wanted zoom
    if (channel && !isNaN(zoom) && !x && !y) {
        channel.zoomTo([zoom], function () {});
    }
    // if channel ready and zoom, x and y given, zoom map to wanted location and zoom
    if (channel && !isNaN(zoom) && x && y) {
        channel.postRequest('MapMoveRequest', [x, y, zoom]);
    }

    handledRef.current = true;
    LOG.log('Handling shared link...');
    if (lang) {
      dispatch(setLocale(lang));
    }

    // If theme given then select wanted theme
    if (themeId && allThemesWithLayers.length > 0) {
        let activateTheme = {};
        allThemesWithLayers.forEach(theme => {
            if (theme.id === parseInt(themeId)) {
                activateTheme = theme;
            } else if (theme.groups && theme.groups.find(t => t.id === parseInt(themeId))) {
                activateTheme = theme.groups.find(t => t.id === parseInt(themeId));
            }
        })

        if (activateTheme) {
          channel.getAllLayersSV((allLayers) => {
            selectTheme(store, channel, allLayers, activateTheme, null, null);
            if (!isMobile) dispatch(setIsThemeMenuOpen(true));
          }, (err) => {
            toast.error(strings.layerlist.errorLoadingLayers, {
              position: "top-center",
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
              theme: "colored",
              transition: Slide
            });
          });
        }
    } else if (maplayers) {
        if (!isMobile) dispatch(setIsSideMenuOpen(true));
        dispatch(setSelectedMapLayersMenuTab(1));
        const layers = maplayers.split('++');
        dispatch(removeAllSelectedLayers());
        layers.reverse().forEach((l, index) => {
            const layerProps = l.split('+');
            if (layerProps.length === 3) {
                const layerId = parseInt(layerProps[0]) || layerProps[0] ;
                // make sure we do not do anything with possible userlayer that is somehow passed in the url
                if (typeof layerId === 'string') return;
                const opacity = parseInt(layerProps[1]);
                const style = layerProps[2];
                channel.postRequest('ChangeMapLayerOpacityRequest', [layerId, opacity]);
                store.dispatch(changeLayerStyle({layerId: layerId, style:style}));
                channel.postRequest('MapModulePlugin.MapLayerVisibilityRequest', [layerId, true]);

                // Update layer orders to correct
                store.dispatch(reArrangeSelectedMapLayers({layerId: layerId, position: index}));
            }
        });
      }

      channel.getLegends((data) => {
        // it's fine to use dispatch here
        dispatch(setLegends(data));
      });

      // pass the store object (not dispatch) to updateLayers
      updateLayers(store, channel);
    
  }, [dispatch, store, channel, allThemesWithLayers, zoom, x, y, maplayers, themeId, lang]);

  return null;
};
